// src/Profile.js
import './Profile.css';
import Post from './Post';
import Screen from './Screen';
import { useNavigate, useLocation } from 'react-router-dom';

import { useProfileContext } from './context/ProfileContext';
import React, { useState, useEffect, useRef } from 'react';
import ReadMoreText from './common/ReadMoreText';

import api, { getMyStakes } from './api';


import backIcon from './assets/backspace-icon.png';
import FollowDrawer from './components/FollowDrawer';
import dotsIcon from './assets/dots-icon.png';
import editIcon from './assets/edit-icon.png';

// NEW
import FilterTabs from './components/FilterTabs';
import AboutSection from './components/profile/AboutSection';
import StakeDetailsDrawer from './components/popups/StakeDetailsDrawer';
import BadgePills from './components/profile/BadgePills';
import AssetsTab from './components/profile/AssetsTab';
import RewardCodeRow from './components/profile/RewardCodeRow';
import FeatureNotAvailableDrawer from './components/popups/FeatureNotAvailableDrawer';
import goldTickIcon from './assets/goldtick-icon.png';
import LoadingSpinner from './components/common/LoadingSpinner';

import ProfileBannerHeader from './components/ProfileBannerHeader';
import ProfileAvatarViewer from './components/ProfileAvatarViewer';

import { canAccessStudio } from './studio/studioAccess';

import {
  createStudioAnalyticsEvent,
  trackStudioEvent,
} from './studio/avatar/services/studioAnalyticsApi';

import PioneerSummaryPanel from './components/PioneerSummaryPanel';
import BottomNav from './components/navigation/BottomNav';
import { useMessageUnreadContext } from './context/MessageUnreadContext';

import pioneerSummaryIcon from './assets/pioneersummary-icon.png';
import {
  recordPioneerSummaryEvent,
} from './services/pioneerSummaryApi';

const DEBUG = true;



function Profile({ user, onLogout, onUserUpdate }) {

  const {
    profilePosts: posts,
    setProfilePosts: setPosts,
    profileOwnerUsername,
    setProfileOwnerUsername,
    setProfileLastFetchedAt,
    addEnrichedPost,
    page,
    setPage,
    hasMore,
    setHasMore,
    hasLoaded,
    setHasLoaded,
    clearProfileState
  } = useProfileContext();



  const [userData, setUserData] = useState(user);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const [showPioneerSummary, setShowPioneerSummary] = useState(false);

  const [showAvatarViewer, setShowAvatarViewer] = useState(false);
  const [summaryFabHidden, setSummaryFabHidden] = useState(false);

const { unreadDmCount } = useMessageUnreadContext();

const [bottomNavHidden, setBottomNavHidden] = useState(false);
const bottomNavScrollAnchorRef = useRef(0);

const handleProfileNavScroll = (event) => {
  const element = event.currentTarget;

  const maxScrollTop = Math.max(
    0,
    element.scrollHeight - element.clientHeight
  );

  const scrollTop = Math.min(
    maxScrollTop,
    Math.max(0, element.scrollTop)
  );

  const delta = scrollTop - bottomNavScrollAnchorRef.current;

  if (scrollTop <= 80) {
    setBottomNavHidden(false);
    bottomNavScrollAnchorRef.current = scrollTop;
    return;
  }

  if (Math.abs(delta) >= 10) {
    setBottomNavHidden(delta > 0);
    bottomNavScrollAnchorRef.current = scrollTop;
  }
};

const getInitialFollowCount = (value) => {
  if (typeof value === 'number') return value;

  if (Array.isArray(value)) {
    const unique = new Set(
      value
        .map((item) => {
          if (typeof item === 'string') return item;
          return item?.username || item?._id || item?.id || '';
        })
        .filter(Boolean)
        .map((item) => String(item).toLowerCase())
    );

    return unique.size;
  }

  return null;
};

const [followerCount, setFollowerCount] = useState(() =>
  getInitialFollowCount(user?.followers)
);

const [followingCount, setFollowingCount] = useState(() =>
  getInitialFollowCount(user?.following)
);

  const [followerList, setFollowerList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [showFollowerList, setShowFollowerList] = useState(false);
  const [showFollowingList, setShowFollowingList] = useState(false);
  const [showMerchantWarning, setShowMerchantWarning] = useState(false);


const [myRewardCodes, setMyRewardCodes] = useState([]);
const [codesLoaded, setCodesLoaded] = useState(false);

// --- Reward Code Marketplace (placeholder drawer) ---
const [sellNAOpen, setSellNAOpen] = useState(false);
const [sellNACode, setSellNACode] = useState(null);

const handleSellRewardCode = (code) => {
  setSellNACode(code || null);
  setSellNAOpen(true);
};


// --- My Stakes state ---
  const [stakedPosts, setStakedPosts] = useState([]);
  const [stakedHasLoaded, setStakedHasLoaded] = useState(false);
  const [stakedLoading, setStakedLoading] = useState(false);
  const [stakedHasMore, setStakedHasMore] = useState(true);
  const [stakedPage, setStakedPage] = useState(0);
  const stakedPageRef = useRef(0);
  const stakedHasMoreRef = useRef(true);
  const stakedLoadingRef = useRef(false);

// --- Stake Details Drawer ---
const [stakeDetailsPostId, setStakeDetailsPostId] = useState(null);
const [stakeDetailsOpen, setStakeDetailsOpen] = useState(false);
const openStakeDetails = (postId) => {
  setStakeDetailsPostId(postId);
  setStakeDetailsOpen(true);
};

  const navigate = useNavigate();

  const profileCacheKey = React.useMemo(() => {
    return user?.username ? `profilePosts:${user.username}` : 'profilePosts:guest';
  }, [user?.username]);


const saveProfileFirstPageCache = React.useCallback(
  (postList) => {
    try {
      const firstPagePosts = Array.isArray(postList)
        ? postList.slice(0, 10).map((post) => {
            /*
             * The post cards use the counts and viewer flags.
             * Large interaction arrays are not needed for instant rendering.
             */
            const {
              likes,
              comments,
              tips,
              saves,
              ...cacheSafePost
            } = post || {};

            return cacheSafePost;
          })
        : [];

      localStorage.setItem(
        profileCacheKey,
        JSON.stringify(firstPagePosts)
      );
    } catch (storageError) {
      console.warn(
        '⚠️ [Profile] Failed to save first-page cache:',
        storageError
      );

      try {
        localStorage.removeItem(profileCacheKey);
      } catch (_) {}
    }
  },
  [profileCacheKey]
);


  const profileIsAdmin = userData?.isAdmin === true || userData?.adminRole != null;
  const profileAdminRole = userData?.adminRole || null;

  const profileIsApp =
    userData?.isApp === true ||
    userData?.accountType === 'app';

  const profileRole =
    profileIsAdmin
      ? 'Admin'
      : profileIsApp
      ? 'App'
      : userData?.userType === 'merchant' && userData?.isMerchant
      ? 'Merchant'
      : 'Pioneer';


const equippedBanner = userData?.equippedProfileBanner || null;

const hasEquippedBanner = Boolean(
  equippedBanner?.imageUrl
);



const feedRef = useRef(null);
const topbarRef = useRef(null);
const roleRef = useRef(null);

const profileOverlayOpen = Boolean(
  showAvatarViewer ||
  showPioneerSummary ||
  showFollowerList ||
  showFollowingList ||
  showMerchantWarning ||
  stakeDetailsOpen ||
  sellNAOpen
);

useEffect(() => {
  const scrollArea = feedRef.current;
  const topbar = topbarRef.current;
  const roleElement = roleRef.current;

  if (!scrollArea || !topbar || !roleElement) return;

let animationFrameId = null;
let lastScrollTop = scrollArea.scrollTop;


const updateTopbarOpacity = () => {
  const scrollTop = scrollArea.scrollTop;

  // Pioneer Summary FAB:
  // visible at the top, hidden scrolling down,
  // visible again when scrolling up.
  if (scrollTop <= 20) {
    setSummaryFabHidden(false);
  } else if (scrollTop > lastScrollTop + 4) {
    setSummaryFabHidden(true);
  } else if (scrollTop < lastScrollTop - 4) {
    setSummaryFabHidden(false);
  }

  lastScrollTop = scrollTop;

  const roleTop = roleElement.offsetTop;

    const fadeStart = 0;
    const fadeEnd = Math.max(100, roleTop - 60);

    const rawProgress =
      (scrollTop - fadeStart) / (fadeEnd - fadeStart);

    const progress = Math.min(
      1,
      Math.max(0, rawProgress)
    );

    topbar.style.setProperty(
      '--profile-topbar-progress',
      String(progress)
    );

    animationFrameId = null;
  };

  const handleScroll = () => {
    if (animationFrameId !== null) return;

    animationFrameId =
      window.requestAnimationFrame(updateTopbarOpacity);
  };

  updateTopbarOpacity();

  scrollArea.addEventListener('scroll', handleScroll, {
    passive: true,
  });

  window.addEventListener('resize', handleScroll);

  return () => {
    scrollArea.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleScroll);

    if (animationFrameId !== null) {
      window.cancelAnimationFrame(animationFrameId);
    }
  };
}, [userData?.username]);


  // NEW: tabs state
  const [selectedTab, setSelectedTab] = useState('my-posts');

  // Refs to avoid stale closures during infinite scroll
  const pageRef = useRef(page || 1);
  const hasMoreRef = useRef(hasMore);
  const loadingPostsRef = useRef(false);

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);


// Instant follow counts from the current user object.
// The API below will still refresh with the latest accurate data.
useEffect(() => {
  if (!user?.username) return;

  setFollowerCount(getInitialFollowCount(user?.followers));
  setFollowingCount(getInitialFollowCount(user?.following));
}, [user?.username, user?.followers, user?.following]);

useEffect(() => { stakedPageRef.current = stakedPage; }, [stakedPage]);
useEffect(() => { stakedHasMoreRef.current = stakedHasMore; }, [stakedHasMore]);
 
 const location = useLocation();
  const scrollToPostId = location.state?.scrollToPostId || null;

  // Smooth scroll to a specific post once its height stabilizes
  useEffect(() => {
    if (!scrollToPostId) return;

    const wrapperId = `post-${scrollToPostId}`;
    let observer = null;
    let heightCheckInterval = null;

    const scrollToStablePost = () => {
      const postEl = document.getElementById(wrapperId);
      if (!postEl || !feedRef.current) return;

      let lastHeight = postEl.offsetHeight;
      let stableCount = 0;

      heightCheckInterval = setInterval(() => {
        const currentHeight = postEl.offsetHeight;
        if (currentHeight === lastHeight) {
          stableCount++;
        } else {
          stableCount = 0;
          lastHeight = currentHeight;
        }

        if (stableCount >= 2) {
          clearInterval(heightCheckInterval);
          const offset = postEl.offsetTop;
          feedRef.current.scrollTo({ top: offset - 100, behavior: 'smooth' });
          postEl.classList.add('highlight-flash');
          setTimeout(() => postEl.classList.remove('highlight-flash'), 3000);
        }
      }, 100);
    };

    const immediatePostEl = document.getElementById(wrapperId);
    if (immediatePostEl) {
      scrollToStablePost();
      return;
    }

    observer = new MutationObserver(() => {
      const el = document.getElementById(wrapperId);
      if (el) {
        scrollToStablePost();
        observer.disconnect();
      }
    });

    observer.observe(document.getElementById('root') || document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (observer) observer.disconnect();
      if (heightCheckInterval) clearInterval(heightCheckInterval);
    };
  }, [posts, scrollToPostId]);

  // Initial fetch (with enrichment) + set hasMore







useEffect(() => {
  const username = user?.username;

  if (!username) return;

  let cancelled = false;

  const sameOwnerInMemory =
    profileOwnerUsername === username &&
    hasLoaded;

  const hasMemoryPosts =
    sameOwnerInMemory &&
    Array.isArray(posts) &&
    posts.length > 0;

  /*
   * If this context already belongs to the logged-in user,
   * keep its posts visible. Never clear valid in-memory posts
   * just because Profile mounted again.
   */
  if (sameOwnerInMemory) {
    setLoading(false);

    if (DEBUG) {
      console.log(
        '⚡ [Profile] Using in-memory posts for',
        username,
        posts.length
      );
    }
  } else {
    /*
     * The context is empty or belongs to a different account.
     * Reset it safely before restoring this user's disk cache.
     */
    setPosts([]);
    setProfileOwnerUsername(username);
    setProfileLastFetchedAt(0);
    setHasLoaded(false);
    setPage(1);
    setHasMore(true);

    pageRef.current = 1;
    hasMoreRef.current = true;

    let restoredCache = false;

    try {
      const raw = localStorage.getItem(profileCacheKey);

      if (raw) {
        const cached = JSON.parse(raw);

        if (Array.isArray(cached)) {
          const firstPageCache = cached.slice(0, 10);

          setPosts(firstPageCache);
          setHasLoaded(true);

          const cachedHasMore = firstPageCache.length === 10;

          setHasMore(cachedHasMore);
          hasMoreRef.current = cachedHasMore;

          saveProfileFirstPageCache(firstPageCache);
          restoredCache = true;

          if (DEBUG) {
            console.log(
              '📦 [Profile] Restored first-page cache for',
              username,
              firstPageCache.length
            );
          }
        }
      }
    } catch (cacheError) {
      console.warn(
        '⚠️ [Profile] Failed to restore cache:',
        cacheError
      );

      try {
        localStorage.removeItem(profileCacheKey);
      } catch (_) {}
    }

    /*
     * Cached posts render immediately. The full spinner is needed
     * only when neither memory nor disk has anything to display.
     */
    setLoading(!restoredCache);
  }

  /*
   * Local stake state belongs only to this mounted Profile screen.
   */
  setStakedPosts([]);
  setStakedHasLoaded(false);
  setStakedPage(0);
  setStakedHasMore(true);

  stakedPageRef.current = 0;
  stakedHasMoreRef.current = true;

  if (feedRef.current) {
    feedRef.current.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }

  const refreshProfilePosts = async () => {
    try {
      const postRes = await api.get(
        `/posts/user/${username}?page=1&limit=10`
      );

      if (cancelled) return;

      let freshPosts = Array.isArray(postRes.data)
        ? postRes.data
        : Array.isArray(postRes.data?.posts)
          ? postRes.data.posts
          : [];

      freshPosts = await enrichPosts(
        freshPosts,
        username
      );

      if (cancelled) return;

      /*
       * If page 2+ was already loaded in context memory, preserve it.
       * Otherwise page one becomes the complete current post state.
       */
      const preserveLoadedPages =
        sameOwnerInMemory &&
        hasMemoryPosts &&
        (pageRef.current || 1) > 1;

      setPosts((previousPosts) => {
        const ownerPosts = Array.isArray(previousPosts)
          ? previousPosts.filter((post) => {
              const authorUsername =
                post?.username ||
                post?.user?.username ||
                '';

              return String(authorUsername) === String(username);
            })
          : [];

        const olderLoadedPosts = preserveLoadedPages
          ? ownerPosts.slice(10)
          : [];

        const freshIds = new Set(
          freshPosts
            .map((post) =>
              String(post?._id || post?.id || '')
            )
            .filter(Boolean)
        );

        const uniqueOlderPosts = olderLoadedPosts.filter((post) => {
          const postId = String(
            post?._id || post?.id || ''
          );

          return postId && !freshIds.has(postId);
        });

        return [
          ...freshPosts,
          ...uniqueOlderPosts,
        ];
      });

      saveProfileFirstPageCache(freshPosts);

      setProfileOwnerUsername(username);
      setProfileLastFetchedAt(Date.now());
      setHasLoaded(true);

      if (!preserveLoadedPages) {
        setPage(1);
        pageRef.current = 1;
      }

      const freshHasMore = freshPosts.length === 10;

      setHasMore(freshHasMore);
      hasMoreRef.current = freshHasMore;

      setStakedPosts((previousPosts) =>
        previousPosts.map((post) => {
          const freshPost = freshPosts.find(
            (item) =>
              String(item?._id) === String(post?._id)
          );

          return freshPost
            ? { ...post, ...freshPost }
            : post;
        })
      );

      if (DEBUG) {
        console.log(
          '🔄 [Profile] Silent page-one refresh complete for',
          username,
          freshPosts.length
        );
      }
    } catch (refreshError) {
      if (!cancelled) {
        console.error(
          '⚠️ Failed to refresh Profile posts:',
          refreshError
        );
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  };

  refreshProfilePosts();

  return () => {
    cancelled = true;
  };

  // This lifecycle intentionally runs only when the profile owner changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user?.username]);





useEffect(() => {
  if (!user?.username) return;

  const loadMyCodes = async () => {
    try {
      const res = await api.get('/rewards/codes/me');
      const items = res?.data?.items || [];
      setMyRewardCodes(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('❌ Failed to load my reward codes:', err);
      setMyRewardCodes([]);
    } finally {
      setCodesLoaded(true);
    }
  };

  loadMyCodes();
}, [user?.username]);



const revealRewardCode = async (codeItem) => {
  const id = codeItem?._id || codeItem?.id;
  if (!id) throw new Error('Missing code id');

  // 1) reveal (backend will lock selling forever)
  const res = await api.post(`/rewards/codes/reveal/${id}`);

  // 2) Optimistic local lock immediately:
  //    ✅ kill sell & buy
  //    ✅ mark revealed
  //    ✅ DO NOT mark redeemed (redeemed happens when used on Plans)
  setMyRewardCodes((prev) =>
    (prev || []).map((c) => {
      const cid = c?._id || c?.id;
      if (String(cid) !== String(id)) return c;

      const currentStatus = String(c?.status || 'active').toLowerCase();

      return {
        ...c,
        canSell: false,
        canBuy: false,
        isRevealed: true,
        revealedAt: c?.revealedAt || res?.data?.revealedAt || new Date().toISOString(),
        status: currentStatus === 'active' ? 'revealed' : c.status,
      };
    })
  );

  // 3) Refresh list from server so everything is guaranteed correct
  try {
    const fresh = await api.get('/rewards/codes/me');
    const items = fresh?.data?.items || [];
    setMyRewardCodes(Array.isArray(items) ? items : []);
  } catch (e) {
    // non-fatal
  }

  return res?.data; // RewardCodeRow expects { code }
};


const deleteRewardCode = async (codeItem) => {
  const id = codeItem?._id || codeItem?.id;
  if (!id) throw new Error('Missing code id');

  // call backend (soft-hide)
  await api.post(`/rewards/codes/hide/${id}`);

  // optimistic remove from UI
  setMyRewardCodes((prev) =>
    (prev || []).filter((c) => String(c?._id || c?.id) !== String(id))
  );

  return { ok: true };
};



// Follow details
useEffect(() => {
  const fetchFollowDetails = async () => {
    if (!user?.username) return;

    try {
      console.log('🟦 [Profile] fetching full follow data for:', user.username);

      const res = await api.get(`/follow/full-follow-data/${user.username}`);

      const followersArr = Array.isArray(res.data?.followers)
        ? res.data.followers
        : [];

      const followingArr = Array.isArray(res.data?.following)
        ? res.data.following
        : [];

      const cleanFollowers = followersArr
        .map((u) => u?.username)
        .filter(Boolean);

      const cleanFollowing = followingArr
        .map((u) => u?.username)
        .filter(Boolean);

      console.log('🟩 [Profile] full follow data response:', {
        followers: followersArr,
        following: followingArr,
        cleanFollowers,
        cleanFollowing,
      });

      // 1) Update drawer lists
      setFollowerList(followersArr);
      setFollowingList(followingArr);

      // 2) Update visible counts
      setFollowerCount(cleanFollowers.length);
      setFollowingCount(cleanFollowing.length);



// Update follow information without overwriting newer profile fields
setUserData((prev) => {
  const cleanedUser = {
    ...(prev || user || {}),
    followers: cleanFollowers,
    following: cleanFollowing,
  };

  if (typeof onUserUpdate === 'function') {
    onUserUpdate(cleanedUser);
  }

  return cleanedUser;
});



    } catch (err) {
      console.error('❌ [Profile] Failed to fetch full follow details:', err);
    }
  };

  fetchFollowDetails();
}, [user?.username]); // eslint-disable-line react-hooks/exhaustive-deps


  const enrichPosts = async (postArray, username) => {
    return Promise.all(
      postArray.map(async (post) => {
        const enriched = { ...post };
        enriched.pinned = !!post.pinned;  // ✅ carry through for UI

// ✅ carry through post background theme fields
enriched.style = post.style || null;
enriched.backgroundTheme =
  post.backgroundTheme ||
  post.style?.backgroundTheme ||
  'normal';

enriched.hasPaidStyle =
  post.hasPaidStyle === true ||
  post.style?.paid === true;


// 🛒 Product repost support
enriched.postType = post.postType || 'post';
enriched.sharedProduct = post.sharedProduct || null;
enriched.productSnapshot = post.productSnapshot || null;

        try {
          const interactionsRes = await api.get(`/posts/interactions/${post._id}`);
          const { likes, comments, tips, saves } = interactionsRes.data;

          enriched.likes = Array.isArray(likes) ? likes : [];
          enriched.comments = Array.isArray(comments) ? comments : [];
          enriched.tipCount = Array.isArray(tips) ? tips.length : 0;
          enriched.likesCount = enriched.likes.length;
          enriched.commentsCount = enriched.comments.length;
          enriched.userHasLiked = enriched.likes.some(u => u.username === username);
          enriched.userHasSaved = Array.isArray(saves) && saves.includes(username);
        } catch (err) {
          console.warn(`❌ Failed to enrich post ${post._id}:`, err);
        }

        enriched.timestamp = post.createdAt;
        enriched.type = 'post';


        enriched.user = post.user || {
          name: post.name,
          username: post.username,
          profilePic: post.profilePic,
          userType: post.userType,
          isMerchant: post.isMerchant,
          isAdmin: post.isAdmin,
          adminRole: post.adminRole,
          isApp: post.isApp,
          accountType: post.accountType,
          location: post.location,
        };


        return enriched;
      })
    );
  };

 


// 🔽 Load page 2+ into React memory only
const loadMorePosts = async () => {
  try {
    const nextPage = (pageRef.current || 1) + 1;

    const res = await api.get(
      `/posts/user/${user.username}?page=${nextPage}&limit=10`
    );

    const raw = Array.isArray(res.data)
      ? res.data
      : Array.isArray(res.data?.posts)
        ? res.data.posts
        : [];

    const enriched = await enrichPosts(
      raw,
      user.username
    );

    setPosts((prev) => {
      const existingIds = new Set(
        (prev || [])
          .map((post) =>
            String(post?._id || post?.id || '')
          )
          .filter(Boolean)
      );

      const newUnique = enriched.filter((post) => {
        const postId = String(
          post?._id || post?.id || ''
        );

        return postId && !existingIds.has(postId);
      });

      if (newUnique.length === 0) {
        return prev;
      }

      /*
       * Keep page 2+ in React state only.
       * Do not save paginated posts to localStorage.
       */
      return [...prev, ...newUnique];
    });

    /*
     * Advance the page only after the request and enrichment succeed.
     */
    pageRef.current = nextPage;
    setPage(nextPage);

    const nextHasMore = enriched.length === 10;

    setHasMore(nextHasMore);
    hasMoreRef.current = nextHasMore;
  } catch (err) {
    console.error(
      '❌ Failed to load more posts (Profile):',
      err
    );
  } finally {
    setLoadingPosts(false);
    loadingPostsRef.current = false;
  }
};





  // 🔗 Attach infinite scroll listener to the scrollable area
  useEffect(() => {

// only run infinite scroll on the active tab
  if (!feedRef.current || loading) return;

    const scrollArea = feedRef.current;
    const handleScroll = () => {
      const nearBottom =
        scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight - 300;

// My Posts pagination
    if (selectedTab === 'my-posts' && nearBottom && hasMoreRef.current && !loadingPostsRef.current) {

        loadingPostsRef.current = true;
        setLoadingPosts(true);
        loadMorePosts();
      }
// My Stakes pagination
    if (selectedTab === 'my-stakes' && nearBottom && stakedHasMoreRef.current && !stakedLoadingRef.current) {
      loadMoreStaked(false);
    }

    };



    scrollArea.addEventListener('scroll', handleScroll);
    // kick it once in case we're already short
    setTimeout(() => handleScroll(), 500);

    return () => {
      scrollArea.removeEventListener('scroll', handleScroll);
    };
    // re-run when count changes so we can fetch next if container is still short

}, [loading, posts.length, stakedPosts.length, selectedTab]);


// 🔽 load next page for My Stakes (group by postId to avoid duplicates)
const loadMoreStaked = async (isFirst = false) => {
  try {
    const nextPage = isFirst ? 1 : (stakedPageRef.current || 0) + 1;
    stakedLoadingRef.current = true;
    setStakedLoading(true);

    const limit = 10;
    const { data } = await getMyStakes({ page: nextPage, limit });
    const stakesArr = Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data) ? data
      : (data?.stakes || []);

    // --- 1) Group stakes by post._id and aggregate ---
    const grouped = new Map();
    const nowMs = Date.now();

    for (const s of stakesArr) {
      const p = s?.post;
      const pid = p?._id;
      if (!pid) continue;

      const exMs = s?.expiresAt ? new Date(s.expiresAt).getTime() : null;

      if (!grouped.has(pid)) {
        grouped.set(pid, {
          post: p,
          totalAmountPi: Number(s?.amountPi || 0),
          // choose the soonest upcoming expiry (smallest future ts)
          nextExpiryMs: exMs && exMs > nowMs ? exMs : null,
        });
      } else {
        const g = grouped.get(pid);
        g.totalAmountPi += Number(s?.amountPi || 0);
        if (exMs && exMs > nowMs) {
          if (!g.nextExpiryMs || exMs < g.nextExpiryMs) g.nextExpiryMs = exMs;
        }
      }
    }

    // --- 2) Turn groups into post cards (one per post) ---
    const groupedPosts = Array.from(grouped.values()).map(({ post: p, totalAmountPi, nextExpiryMs }) => ({
      ...p,
      timestamp: p.createdAt,

      user: p.user || {
        name: p.name,
        username: p.username,
        profilePic: p.profilePic,
        userType: p.userType,
        isMerchant: p.isMerchant,
        isAdmin: p.isAdmin,
        adminRole: p.adminRole,
        isApp: p.isApp,
        accountType: p.accountType,
        location: p.location,
      },

      pinned: !!p.pinned,
      type: 'post',
      // Prefer server-computed fields if present; else our aggregates
      stakeActivePi: typeof p.stakeActivePi === 'number' ? p.stakeActivePi : totalAmountPi,
      stakeNextExpiryAt: p.stakeNextExpiryAt || (nextExpiryMs ? new Date(nextExpiryMs).toISOString() : null),
    }));

    // --- 3) Enrich (likes/comments/saves, etc.) ---
    const enriched = await enrichPosts(groupedPosts, user.username);

    // --- 4) Merge into state by post._id (update if exists, else insert) ---
    setStakedPosts(prev => {
      const byId = new Map(prev.map(pp => [pp._id, pp]));
      for (const np of enriched) {
        const existing = byId.get(np._id);
        if (existing) {
          // keep latest meta + update stake aggregates
          byId.set(np._id, {
            ...existing,
            ...np,
            stakeActivePi: typeof np.stakeActivePi === 'number'
              ? np.stakeActivePi
              : (typeof existing.stakeActivePi === 'number' ? existing.stakeActivePi : 0),
            stakeNextExpiryAt: np.stakeNextExpiryAt || existing.stakeNextExpiryAt || null,
          });
        } else {
          byId.set(np._id, np);
        }
      }
      return Array.from(byId.values());
    });

    // --- 5) Update paging flags ---
    setStakedPage(nextPage);
    setStakedHasLoaded(true);
    const serverHasMore = Boolean(data?.hasMore ?? (stakesArr.length === limit));
    setStakedHasMore(serverHasMore);
    stakedHasMoreRef.current = serverHasMore;

  } catch (err) {
    console.error('❌ Failed to load staked posts:', err);
    setStakedHasMore(false);
    stakedHasMoreRef.current = false;
  } finally {
    setStakedLoading(false);
    stakedLoadingRef.current = false;
  }
};





const handleDeletePost = (deletedPostId) => {
  setPosts((prev) => {
    const updated = prev.filter(
      (post) =>
        String(post?._id) !== String(deletedPostId)
    );

    saveProfileFirstPageCache(updated);

    return updated;
  });

  setStakedPosts((prev) =>
    prev.filter(
      (post) =>
        String(post?._id) !== String(deletedPostId)
    )
  );
};




// save languages (owner) — optimistic + app-state update + server reconcile
const handleSaveLanguages = async (newLangs) => {
  const optimisticUser = {
    ...(userData || {}),
    about: { ...(userData?.about || {}), languages: newLangs },
  };

  // 1) Optimistic local
  setUserData(optimisticUser);

  // 2) Update app-level user immediately (so navigating away/back stays fresh)
  onUserUpdate && onUserUpdate(optimisticUser);

  // 3) Server patch
  try {
    const { data: updated } = await api.patch(`/users/me?_=${Date.now()}`, {
      languages: newLangs,
    });

    // Prefer server truth if it returns a user object


    if (updated && typeof updated === 'object') {
      const mergedUpdatedUser = {
        ...(userData || {}),
        ...updated,

        // ✅ preserve app ownership data already loaded from About API
        accountType:
          updated?.accountType ||
          userData?.accountType ||
          'primary',

        apps: Array.isArray(updated?.apps)
          ? updated.apps
          : (Array.isArray(userData?.apps) ? userData.apps : []),

        // ✅ preserve normalized about structure while updating languages
        about: {
          ...(userData?.about || {}),
          ...(updated?.about || {}),
          languages: newLangs,
          links:
            updated?.about?.links ||
            userData?.about?.links ||
            {},
          badges: Array.isArray(updated?.about?.badges)
            ? updated.about.badges
            : (Array.isArray(userData?.about?.badges) ? userData.about.badges : []),
        },
      };

      setUserData(mergedUpdatedUser);
      onUserUpdate && onUserUpdate(mergedUpdatedUser);
    } else {

      // Persist optimistic if server didn’t return the user doc
      try { localStorage.setItem('barterfi_user', JSON.stringify(optimisticUser)); } catch {}
    }
  } catch (err) {
    console.error('❌ Failed to save languages:', err);
    alert('Failed to save languages. Please try again.');
    // Optional: rollback to userData before optimistic change if you want strict consistency
  }
};




// Load the complete owner profile, including equippedProfileBanner
useEffect(() => {
  if (!user?.username) return;

  let cancelled = false;

  const loadFullProfile = async () => {
    try {
      const response = await api.get(`/users/${user.username}`);

      const freshProfile =
        response?.data?.user ||
        response?.data?.profile ||
        response?.data;

      if (
        cancelled ||
        !freshProfile ||
        typeof freshProfile !== 'object'
      ) {
        return;
      }

      setUserData((prev) => {
        const base =
          prev && prev.username === user.username
            ? prev
            : user;

        return {
          ...base,
          ...freshProfile,

          equippedProfileBanner:
            freshProfile?.equippedProfileBanner || null,

          about: {
            ...(base?.about || {}),
            ...(freshProfile?.about || {}),
          },

          apps: Array.isArray(freshProfile?.apps)
            ? freshProfile.apps
            : Array.isArray(base?.apps)
              ? base.apps
              : [],
        };
      });
    } catch (error) {
      console.error(
        '❌ [Profile] Failed to load full profile:',
        error
      );
    }
  };

  loadFullProfile();

  return () => {
    cancelled = true;
  };
}, [user?.username]);



// ✅ Load About API data (apps, accountType, normalized about fields)
useEffect(() => {
  if (!user?.username) return;

  const loadAboutData = async () => {
    try {
      const { data: aboutData } = await api.get(`/users/${user.username}/about`);

      setUserData(prev => {
        const base = prev && prev.username === user.username ? prev : (user || {});

        return {
          ...base,
          accountType: aboutData?.accountType || base?.accountType || 'primary',
          apps: Array.isArray(aboutData?.apps) ? aboutData.apps : [],
          about: {
            ...(base?.about || {}),
            languages: Array.isArray(aboutData?.languages) ? aboutData.languages : [],
            links: aboutData?.links || (base?.about?.links || {}),
            badges: Array.isArray(aboutData?.badges) ? aboutData.badges : [],
          },
        };
      });

      if (DEBUG) {
        console.log('[Profile] merged about data:', {
          username: user.username,
          accountType: aboutData?.accountType,
          apps: aboutData?.apps || [],
        });
      }
    } catch (err) {
      console.error('❌ [Profile] Failed to load about data:', err);
    }
  };

  loadAboutData();
}, [user?.username]);



// First-load My Stakes when the tab is opened
useEffect(() => {
  if (selectedTab !== 'my-stakes') return;
  if (stakedHasLoaded || stakedLoading) return;
  const prime = async () => {
    await loadMoreStaked(true);
  };
  prime();
}, [selectedTab]); // eslint-disable-line react-hooks/exhaustive-deps






  // Profile.js  (below the post:deleted listener)
  useEffect(() => {
    const onExternalCreate = async (e) => {
      const rawPost = e?.detail?.post;
      if (!rawPost) return;

      // Only care about posts authored by this profile owner
      const authorUsername =
        rawPost.user?.username ||
        rawPost.username ||
        '';

      if (!authorUsername || authorUsername !== user.username) {
        return;
      }

      try {
        // 🔍 Enrich the single post (likes/comments/saves etc.)
        const [enriched] = await enrichPosts([rawPost], user.username);

        setPosts(prev => {
          // Avoid duplicates if it’s somehow already in the list
          const exists = prev.some(p => p._id === enriched._id);
          if (exists) return prev;

  
const next = [enriched, ...prev];

saveProfileFirstPageCache(next);

return next;


        });
      } catch (err) {
        console.error('❌ Failed to enrich new post for Profile:', err);
      }
    };

    window.addEventListener('post:created', onExternalCreate);
    return () => window.removeEventListener('post:created', onExternalCreate);

}, [
  user?.username,
  profileCacheKey,
  setPosts,
  saveProfileFirstPageCache,
]);


  // Profile.js  (add below your other useEffect hooks)
  useEffect(() => {
    const onExternalDelete = (e) => {
      const postId = e?.detail?.postId;
      if (!postId) return;

      // Remove from state immediately
      setPosts(prev => {
        const updated = prev.filter(p => p._id !== postId);
        // keep Profile cache in sync so it doesn't resurrect


saveProfileFirstPageCache(updated);

return updated;   

   });
setStakedPosts(prev => prev.filter(p => p._id !== postId));
    };

    window.addEventListener('post:deleted', onExternalDelete);
    return () => window.removeEventListener('post:deleted', onExternalDelete);

}, [
  profileCacheKey,
  setPosts,
  saveProfileFirstPageCache,
]);

  return (
    <Screen>


<div
  className={`profile-screen-container ${
    hasEquippedBanner
      ? 'profile-screen-container-with-banner'
      : ''
  }`}
>

    <div
      className="profile-topbar"
      ref={topbarRef}
    >


<button
  type="button"
  className="profile-topbar-icon-btn profile-topbar-back-btn"
  onClick={() => navigate('/home', { replace: true })}
  aria-label="Go back"
>
  <img
    src={backIcon}
    alt=""
    className="back-icon-profile"
  />
</button>



 <h2 className="profile-title">Profile</h2>






<div className="dots-container">
  <button
    type="button"
    className="profile-topbar-icon-btn menu-toggle"
    onClick={() => navigate('/profile-menu')}
    aria-label="Open profile menu"
  >
    <img
      src={dotsIcon}
      alt=""
      className="profile-dots-icon"
    />
  </button>
</div>



        </div>


<div
  className="profile-scroll-area"
  ref={feedRef}
  onScroll={handleProfileNavScroll}
>

          <div className="profile-content-inner">





<div
  className={`profile-header-centered ${
    hasEquippedBanner ? 'profile-header-centered-with-banner' : ''
  }`}
>



{hasEquippedBanner ? (
  <ProfileBannerHeader
    banner={equippedBanner}
    profilePic={
      userData?.profilePic ||
      require('./assets/placeholder-profile.png')
    }
    name={userData?.name}
    isOwnProfile={true}
    isSpecialAvatar={profileIsAdmin || profileIsApp}
    onViewAvatar={() => setShowAvatarViewer(true)}
    onEditProfile={() => navigate('/edit-profile')}
    editIcon={editIcon}
  />
) : (
  <div className="profile-avatar-edit-wrap">
    <button
      type="button"
      className={`profile-avatar-view-btn ${
        profileIsAdmin || profileIsApp
          ? 'profile-avatar-view-btn-special'
          : ''
      }`}
      onClick={() => setShowAvatarViewer(true)}
      aria-label="View my profile picture"
      aria-haspopup="dialog"
    >
      <img
        src={
          userData?.profilePic ||
          require('./assets/placeholder-profile.png')
        }
        alt={`${userData?.name || 'My'} avatar`}
        className={`avatar-large ${
          profileIsAdmin || profileIsApp
            ? 'avatar-large-admin'
            : ''
        }`}
      />
    </button>

    <button
      type="button"
      className="profile-avatar-edit-btn"
      onClick={() => navigate('/edit-profile')}
      aria-label="Edit profile"
    >
      <img
        src={editIcon}
        alt=""
        className="profile-avatar-edit-icon"
      />
    </button>
  </div>
)}






<div className="profile-name-row">
  <strong className="profile-name">{userData.name}</strong>

  {(profileIsAdmin || profileIsApp) && (
    <img
      src={goldTickIcon}
      alt={profileIsAdmin ? 'Admin verified' : 'App verified'}
      className="profile-gold-tick"
      title={profileIsAdmin ? (profileAdminRole || 'Admin') : 'App account'}
    />
  )}

</div>


          <span
            className="role-badge"
            ref={roleRef}
          >
            {profileRole}
          </span>


              {userData.bio && (
                <div className="user-bio">
                  <ReadMoreText text={userData.bio} maxLines={3} />
                </div>
              )}
              {userData.location && <p className="location-text">{userData.location}</p>}


<div className="follow-stats-centered">
  <span className="follower-link" onClick={() => setShowFollowingList(true)}>
    {followingCount ?? '—'} Following
  </span>
  {' • '}
  <span className="follower-link" onClick={() => setShowFollowerList(true)}>
    {followerCount ?? '—'} Followers
  </span>
</div>

            </div>

            {/* TABS */}
            <div style={{ padding: '8px 14px 0' }}>
              <FilterTabs



tabs={[
  { key: 'my-posts', label: 'Posts' },
  { key: 'my-stakes', label: 'Stakes' },
  { key: 'badges', label: 'Badges' },
  { key: 'assets', label: 'Assets' },
  { key: 'about', label: 'About' },
]}


                value={selectedTab}
                onChange={setSelectedTab}
                ariaLabel="Profile sections"
              />
            </div>

            {/* TAB CONTENT */}

{selectedTab === 'about' && (
  <AboutSection
    user={userData}
    canVisitStore={
      userData?.userType === 'merchant' &&
      userData?.isMerchant === true &&
      userData?.status === 'approved'
    }
    onVisitStore={() =>
      userData?.businessUsername && navigate(`/store/${userData.businessUsername}`)
    }
    isOwner={true}                          // ✅ owner can edit
    onSaveLanguages={handleSaveLanguages}   // ✅ wire save
  />
)}


{selectedTab === 'badges' && (
  <div className="badge-tab-wrap">
    <BadgePills
      badges={userData?.badges || []}
      username={userData?.username}
      stacked     
      animate
    />
  </div>
)}


{selectedTab === 'assets' && (
  <div className="assets-tab-wrap">


<RewardCodeRow
  items={myRewardCodes}
  mode="owner"
  onSell={handleSellRewardCode}
  onRedeem={revealRewardCode}
  onDelete={deleteRewardCode}   // ✅ NEW
/>



  </div>
)}



{/* ✅ ADD THIS NEW BLOCK HERE */}

{selectedTab === 'my-stakes' && (
  <div className="user-posts profile-post-feed profile-stakes-feed">

    {!stakedHasLoaded && stakedLoading ? (


<LoadingSpinner size="medium" />

    ) : stakedPosts.length === 0 ? (
      <p>You haven’t staked any posts yet.</p>
    ) : (
      <>
        {stakedPosts.map(post => {
          const renderKey =
            `${post._id}-${post.likesCount}-${post.commentsCount}-${post.tipCount}-${post.userHasLiked}-${post.userHasSaved}`;
          return (
<Post
  key={renderKey}
  id={post._id}
  name={post.name}
  username={post.username}
  profilePic={post.profilePic}
  location={post.location}
  content={post.content}
  mentions={post.mentions}
  media={post.media}
  mediaType={post.mediaType}
  transactionId={post.transactionId}
  poll={post.poll || null}
  userType={post.userType}
  isMerchant={post.isMerchant}
  isApp={post.isApp}
  accountType={post.accountType}
  user={post.user}

  timestamp={post.timestamp}

  likesCount={post.likesCount}
  commentsCount={post.commentsCount}
  viewCount={Number(post.viewCount || 0)}
  tipCount={post.tipCount}

  userHasLiked={post.userHasLiked}
  userHasSaved={post.userHasSaved}

currentUser={user.username}
currentUserProfilePic={
  userData?.profilePic ||
  user?.profilePic
}
currentScreen="profile"

  wrapperId={`post-${post._id}`}
  onDelete={handleDeletePost}
  pinned={post.pinned === true}
  stakeActivePi={Number(post.stakeActivePi || 0)}
  stakeNextExpiryAt={post.stakeNextExpiryAt || null}
  onStakeDetailsClick={() => openStakeDetails(post._id)}


isRepost={post.isRepost === true || post.type === 'repost'}
repostOf={post.repostOf}
repostComment={post.repostComment || ''}

/* 🛒 Product repost support */
postType={post.postType || 'post'}
sharedProduct={post.sharedProduct || null}
productSnapshot={post.productSnapshot || null}

/* ✅ Post background theme support */
style={post.style || null}

backgroundTheme={
  post.backgroundTheme ||
  post.style?.backgroundTheme ||
  'normal'
}
hasPaidStyle={
  post.hasPaidStyle === true ||
  post.style?.paid === true
}

/* ✅ Optional Post Action */
postAction={post.postAction || null}
/>

          );
        })}

{stakedLoading && <LoadingSpinner size="medium" />}

        {!stakedHasMore && !stakedLoading && stakedPosts.length > 0 && (
          <div className="end-of-feed-msg"><p>End of staked posts.</p></div>
        )}
      </>
    )}
  </div>
)}


{selectedTab === 'my-posts' && (
  <div className="user-posts profile-post-feed profile-main-post-feed">

                {loading && !hasLoaded ? (


<LoadingSpinner size="medium" />


                ) : posts.length === 0 ? (
                  <p>No posts yet.</p>
                ) : (
                  <>
                    {posts.map(post => {
                      const renderKey = `${post._id}-${post.likesCount}-${post.commentsCount}-${post.tipCount}-${post.userHasLiked}-${post.userHasSaved}`;
                      return (

<Post
  key={renderKey}
  id={post._id}
  name={post.name}
  username={post.username}
  profilePic={post.profilePic}
  location={post.location}
  content={post.content}
  mentions={post.mentions}
  media={post.media}
  mediaType={post.mediaType}
  transactionId={post.transactionId}
  poll={post.poll || null}
  userType={post.userType}
  isMerchant={post.isMerchant}
  isApp={post.isApp}
  accountType={post.accountType}
  user={post.user}


  timestamp={post.timestamp}

  likesCount={post.likesCount}
  commentsCount={post.commentsCount}
  viewCount={Number(post.viewCount || 0)}
  tipCount={post.tipCount}

  userHasLiked={post.userHasLiked}
  userHasSaved={post.userHasSaved}

currentUser={user.username}
currentUserProfilePic={
  userData?.profilePic ||
  user?.profilePic
}
currentScreen="profile"

  wrapperId={`post-${post._id}`}
  onDelete={handleDeletePost}
  pinned={post.pinned === true}
  onStakeDetailsClick={() => openStakeDetails(post._id)}

isRepost={post.isRepost === true || post.type === 'repost'}
repostOf={post.repostOf}
repostComment={post.repostComment || ''}

/* 🛒 Product repost support */
postType={post.postType || 'post'}
sharedProduct={post.sharedProduct || null}
productSnapshot={post.productSnapshot || null}

/* ✅ Post background theme support */
style={post.style || null}

backgroundTheme={
  post.backgroundTheme ||
  post.style?.backgroundTheme ||
  'normal'
}
hasPaidStyle={
  post.hasPaidStyle === true ||
  post.style?.paid === true
}

/* ✅ Optional Post Action */
postAction={post.postAction || null}
/>

                      );
                    })}

{loadingPosts && <LoadingSpinner size="medium" />}

                    {!hasMore && !loadingPosts && posts.length > 0 && (
                      <div className="end-of-feed-msg"><p>You've reached the end.</p></div>
                    )}
                  </>
                )}
              </div>
            )}


          </div>
        </div>

        {showMerchantWarning && (
          <div className="modal-backdrop" onClick={() => setShowMerchantWarning(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <p>{showMerchantWarning}</p>
              <button className="merchant-ok-btn" onClick={() => setShowMerchantWarning(false)}>OK</button>
            </div>
          </div>
        )}

        {showFollowerList && (
          <FollowDrawer isOpen={showFollowerList} title="Followers" list={followerList} onClose={() => setShowFollowerList(false)} />
        )}

        {showFollowingList && (
          <FollowDrawer isOpen={showFollowingList} title="Following" list={followingList} onClose={() => setShowFollowingList(false)} />
        )}
      </div>




{stakeDetailsOpen && (
  <StakeDetailsDrawer
    isOpen={stakeDetailsOpen}
    postId={stakeDetailsPostId}
    onClose={() => setStakeDetailsOpen(false)}
  />
)}



<FeatureNotAvailableDrawer
  open={sellNAOpen}
  title="Reward Code Marketplace"
  onClose={() => {
    setSellNAOpen(false);
    setSellNACode(null);
  }}
/>


<button
  type="button"


className={`profile-summary-fab ${
  summaryFabHidden || profileOverlayOpen ? 'is-hidden' : ''
}`}

onClick={() => {
  if (!user?.username) return;

  setShowPioneerSummary(true);
  recordPioneerSummaryEvent(
    user.username,
    'summary_opened',
    'profile'
  ).catch((err) => {
    console.warn(
      'Summary open event unavailable:',
      err?.response?.status || err?.message
    );
  });
}}
  aria-label="View my summary"
  title="View my summary"
>
  <img src={pioneerSummaryIcon} alt="" />
</button>

{showPioneerSummary && user?.username && (

<PioneerSummaryPanel
  username={user.username}
  entryPoint="profile"
  onClose={() => setShowPioneerSummary(false)}
/>
)}




{showAvatarViewer && (
  <ProfileAvatarViewer
    profilePic={userData?.profilePic}
    name={userData?.name || user?.name || user?.username}
    onClose={() => setShowAvatarViewer(false)}


    onCreateAvatar={
      canAccessStudio(user)
        ? () => {
            void trackStudioEvent(
              createStudioAnalyticsEvent(
                'studio_entry_clicked',
                { entryPoint: 'profile' }
              )
            );

            setShowAvatarViewer(false);

            navigate('/studio/avatar', {
              state: {
                studioEntryPoint: 'profile',
              },
            });
          }
        : undefined
    }

  />
)}


      <BottomNav
        user={user}
        profileSrc={userData?.profilePic || user?.profilePic}
        unreadDmCount={unreadDmCount}
        hidden={bottomNavHidden || profileOverlayOpen}
      />
    </Screen>
  );
}

export default Profile;
